import React, { ReactNode } from 'react';

import { AlertDialog, Button } from '@fhs/ui';
import logger from 'utils/logger';
import { GENERIC_DIALOG_CANCEL_BUTTON, GENERIC_DIALOG_CONFIRM_BUTTON } from 'utils/test-ids';

export type IConfirmProps = {
  testID?: string;
  showDialog: boolean;
  onDismiss?: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  headingComponent?: ReactNode;
  heading?: string;
  bodyComponent?: ReactNode;
  body?: string;
  confirmLabel?: string | ReactNode;
  cancelLabel?: string | ReactNode;
  modalAppearanceEventMessage?: string;
  ariaLabel?: string;
};

function ConfirmDialog({
  confirmLabel = 'Okay',
  cancelLabel = 'Cancel',
  testID,
  showDialog,
  onDismiss,
  onConfirm,
  onCancel,
  headingComponent,
  heading,
  bodyComponent,
  body,
  ariaLabel,
  ...props
}: IConfirmProps) {
  if (__DEV__ && Object.keys(props).length > 1) {
    logger.warn(`ConfirmDialog provided unsupported props: ${JSON.stringify(props, null, 2)}`);
  }

  const normalizeAriaLabel = (label: unknown) =>
    typeof label === 'string' && label.trim().length > 0 ? label : undefined;
  const derivedLabel = normalizeAriaLabel(ariaLabel) ?? normalizeAriaLabel(heading);

  return (
    <AlertDialog
      testID={testID}
      visible={showDialog}
      accessibilityLabel={derivedLabel}
      onClose={() => {
        if (onDismiss) {
          onDismiss?.();
        } else {
          onCancel();
        }
      }}
      onRequestClose={onDismiss}
      headingContent={headingComponent ?? heading}
      bodyContent={bodyComponent ?? body}
      actions={
        <>
          <Button size="xl" type="solid" onPress={onConfirm} testID={GENERIC_DIALOG_CONFIRM_BUTTON}>
            {typeof confirmLabel === 'string' ? (
              <Button.Text>{confirmLabel}</Button.Text>
            ) : (
              confirmLabel
            )}
          </Button>
          <Button size="xl" type="outline" onPress={onCancel} testID={GENERIC_DIALOG_CANCEL_BUTTON}>
            {typeof cancelLabel === 'string' ? (
              <Button.Text>{cancelLabel}</Button.Text>
            ) : (
              cancelLabel
            )}
          </Button>
        </>
      }
    />
  );
}

export default ConfirmDialog;
